import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Check,
  MapPin,
  Pencil,
  X,
  Plus,
  Loader2,
  Sparkles,
  UploadCloud,
  Briefcase,
  GraduationCap,
  Award,
  Building2,
  Calendar,
  DollarSign,
  Trash2,
  Target,
  Layers,
  TrendingUp,
  CheckCircle2,
  ChevronRight,
  Code2,
  Cpu,
  Server,
  Workflow,
} from "lucide-react";
import PageHead from "@/components/PageHead";
import { MatchRing } from "@/components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CURRENCIES } from "@/data/mockData";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const WORK_MODELS = ["Remote", "Hybrid", "Onsite"];

export default function CareerPassport() {
  const { user, updateProfile } = useAuth();
  const fileInputRef = useRef(null);
  const p = user?.profile || {};
  const rawSkills = p.skills || [];

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "experience" | "skills" | "education" | "preferences"
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const currentRole = p.current_role || "Software Engineer";
  const targetRole = p.target_role || "Senior Software Engineer";
  const userName = user?.name || "Candidate Profile";
  const yearsExp = p.years_experience ?? 4;
  const strength = p.profile_strength || 90;
  const primaryLocation = (p.locations && p.locations[0]) || "Ahmedabad, Gujarat";
  const targetSalary = p.target_salary || "$140k";

  // Bio summary
  const userBio =
    p.bio ||
    `High-performing ${currentRole} with ${yearsExp}+ years of hands-on expertise building scalable distributed systems, high-availability microservices, and modern user-centric applications. Passionate about engineering excellence, clean architecture, and driving measurable business outcomes.`;

  // Categorize skills into logical buckets
  const categorizedSkills = p.skills_categorized || {
    languages: rawSkills.filter((s) => /java|python|c\+\+|c#|typescript|javascript|golang|go|rust|ruby|php|sql/i.test(s)),
    frameworks: rawSkills.filter((s) => /spring|boot|react|next|node|angular|vue|django|flask|express|fastapi|dotnet|\.net/i.test(s)),
    cloud_tools: rawSkills.filter((s) => /aws|azure|gcp|docker|kubernetes|postgres|sql|mongo|redis|kafka|graphql|git|ci\/cd|linux/i.test(s)),
    competencies: rawSkills.filter((s) => !/java|python|c\+\+|c#|typescript|javascript|golang|go|rust|ruby|php|spring|boot|react|next|node|angular|vue|django|flask|express|fastapi|aws|azure|gcp|docker|kubernetes|postgres|mongo|redis|kafka|graphql|git|ci\/cd|linux/i.test(s)),
  };

  // Fallback defaults for rich display if profile is freshly created
  const experiences = Array.isArray(p.experiences) && p.experiences.length > 0
    ? p.experiences
    : [
        {
          id: "exp-1",
          title: currentRole,
          company: "Enterprise Technology Solutions",
          period: "2022 – Present",
          location: `${primaryLocation} · Hybrid`,
          type: "Full-Time",
          summary: `Lead core backend & application feature engineering, architectural modernization, and performance tuning for distributed web services.`,
          achievements: [
            `Engineered distributed microservices architecture handling 45k+ daily requests with 99.98% uptime.`,
            `Optimized relational query response times by 38% through indexing, connection pooling, and multi-tier Redis caching.`,
            `Automated continuous integration and deployment pipelines, reducing release deployment cycles from 4 hours to 18 minutes.`
          ],
          technologies: rawSkills.slice(0, 5).length > 0 ? rawSkills.slice(0, 5) : ["Java", "Spring Boot", "React", "PostgreSQL", "Docker"]
        },
        {
          id: "exp-2",
          title: `Associate ${currentRole}`,
          company: "Global Digital Systems",
          period: "2020 – 2022",
          location: "Remote",
          type: "Full-Time",
          summary: `Contributed to end-to-end full-stack feature delivery, REST API integrations, and unit test automation suites.`,
          achievements: [
            `Built modular RESTful APIs and UI components adopted across 6 internal business units.`,
            `Maintained 90%+ code coverage through rigorous unit and integration testing.`
          ],
          technologies: rawSkills.slice(2, 6).length > 0 ? rawSkills.slice(2, 6) : ["Java", "React", "SQL", "Git"]
        }
      ];

  const education = Array.isArray(p.education) && p.education.length > 0
    ? p.education
    : [
        {
          id: "edu-1",
          degree: "Bachelor of Technology (B.Tech)",
          field: "Computer Science & Engineering",
          institution: "Gujarat Technological University",
          year: "2020",
          grade: "8.6 CGPA · First Class with Distinction"
        }
      ];

  const certifications = Array.isArray(p.certifications) && p.certifications.length > 0
    ? p.certifications
    : [
        {
          id: "cert-1",
          name: "AWS Certified Solutions Architect / Developer",
          issuer: "Amazon Web Services (AWS)",
          year: "2023"
        },
        {
          id: "cert-2",
          name: "Certified Enterprise Java & Spring Developer",
          issuer: "Oracle / Enterprise Academy",
          year: "2022"
        }
      ];

  const parseWorkModels = (val) => {
    if (Array.isArray(val)) return val.filter(Boolean);
    if (!val) return ["Onsite", "Hybrid"];
    return String(val)
      .split(/[,/·|]/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const parseSalary = (val) => {
    if (!val) return { currency: "USD", amount: "140k" };
    const str = String(val).trim();
    for (const c of CURRENCIES) {
      if (str.startsWith(c.code)) {
        return { currency: c.code, amount: str.slice(c.code.length).trim() };
      }
      if (str.startsWith(c.symbol)) {
        return { currency: c.code, amount: str.slice(c.symbol.length).trim() };
      }
    }
    return { currency: "USD", amount: str };
  };

  const initForm = () => {
    const salaryParsed = parseSalary(p.target_salary || "$140k");
    const currentModels = parseWorkModels(p.work_models || p.work_model || ["Onsite", "Hybrid"]);
    return {
      name: user?.name || "",
      bio: userBio,
      current_role: currentRole,
      target_role: targetRole,
      years_experience: yearsExp,
      location: primaryLocation,
      work_models: currentModels.length > 0 ? currentModels : ["Onsite", "Hybrid"],
      salary_currency: salaryParsed.currency,
      target_salary: salaryParsed.amount,
      skills: [...rawSkills],
      experiences: JSON.parse(JSON.stringify(experiences)),
      education: JSON.parse(JSON.stringify(education)),
      certifications: JSON.parse(JSON.stringify(certifications)),
    };
  };

  const [form, setForm] = useState(initForm);
  const [skillInput, setSkillInput] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleWorkModel = (m) => {
    setForm((f) => {
      const list = f.work_models || [];
      if (list.includes(m)) {
        if (list.length === 1) return f;
        return { ...f, work_models: list.filter((x) => x !== m) };
      }
      return { ...f, work_models: [...list, m] };
    });
  };

  const openEdit = (tab = "overview") => {
    setForm(initForm());
    setSkillInput("");
    setActiveTab(tab);
    setOpen(true);
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s || form.skills.includes(s)) {
      setSkillInput("");
      return;
    }
    setForm((f) => ({ ...f, skills: [...f.skills, s] }));
    setSkillInput("");
  };

  const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

  // Experience form helpers
  const addExperienceItem = () => {
    const newItem = {
      id: `exp-${Date.now()}`,
      title: "Software Engineer",
      company: "Company Name",
      period: "2023 – Present",
      location: "Hybrid / Remote",
      type: "Full-Time",
      summary: "Core role responsibility overview...",
      achievements: ["Delivered key architectural milestones with measurable business impact."],
      technologies: ["Java", "Spring Boot", "React"]
    };
    setForm((f) => ({ ...f, experiences: [newItem, ...(f.experiences || [])] }));
  };

  const updateExperienceItem = (index, field, value) => {
    setForm((f) => {
      const updated = [...(f.experiences || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, experiences: updated };
    });
  };

  const removeExperienceItem = (index) => {
    setForm((f) => ({
      ...f,
      experiences: (f.experiences || []).filter((_, i) => i !== index),
    }));
  };

  // Education form helpers
  const addEducationItem = () => {
    const newItem = {
      id: `edu-${Date.now()}`,
      degree: "Bachelor of Technology",
      field: "Computer Science",
      institution: "University / Institute Name",
      year: "2021",
      grade: "First Class"
    };
    setForm((f) => ({ ...f, education: [...(f.education || []), newItem] }));
  };

  const updateEducationItem = (index, field, value) => {
    setForm((f) => {
      const updated = [...(f.education || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, education: updated };
    });
  };

  const removeEducationItem = (index) => {
    setForm((f) => ({
      ...f,
      education: (f.education || []).filter((_, i) => i !== index),
    }));
  };

  // Certifications form helpers
  const addCertItem = () => {
    const newItem = {
      id: `cert-${Date.now()}`,
      name: "Professional Cloud Certificate",
      issuer: "Certifying Body",
      year: "2023"
    };
    setForm((f) => ({ ...f, certifications: [...(f.certifications || []), newItem] }));
  };

  const updateCertItem = (index, field, value) => {
    setForm((f) => {
      const updated = [...(f.certifications || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, certifications: updated };
    });
  };

  const removeCertItem = (index) => {
    setForm((f) => ({
      ...f,
      certifications: (f.certifications || []).filter((_, i) => i !== index),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const currSym = CURRENCIES.find((c) => c.code === (form.salary_currency || "USD"))?.symbol || "$";
      const fullSalary = form.target_salary
        ? form.target_salary.startsWith("$") ||
          form.target_salary.startsWith("€") ||
          form.target_salary.startsWith("£") ||
          form.target_salary.startsWith("₹")
          ? form.target_salary
          : `${currSym}${form.target_salary}`
        : `${currSym}140k`;

      const workModelJoined = form.work_models.join(" / ");

      // Re-categorize updated skills
      const updatedCatSkills = {
        languages: form.skills.filter((s) => /java|python|c\+\+|c#|typescript|javascript|golang|go|rust|ruby|php|sql/i.test(s)),
        frameworks: form.skills.filter((s) => /spring|boot|react|next|node|angular|vue|django|flask|express|fastapi|dotnet|\.net/i.test(s)),
        cloud_tools: form.skills.filter((s) => /aws|azure|gcp|docker|kubernetes|postgres|sql|mongo|redis|kafka|graphql|git|ci\/cd|linux/i.test(s)),
        competencies: form.skills.filter((s) => !/java|python|c\+\+|c#|typescript|javascript|golang|go|rust|ruby|php|spring|boot|react|next|node|angular|vue|django|flask|express|fastapi|aws|azure|gcp|docker|kubernetes|postgres|mongo|redis|kafka|graphql|git|ci\/cd|linux/i.test(s)),
      };

      await updateProfile({
        name: form.name,
        profile: {
          bio: form.bio,
          current_role: form.current_role,
          target_role: form.target_role,
          years_experience: parseFloat(form.years_experience) || 0,
          locations: [form.location, ...(p.locations || []).slice(1)].filter(Boolean),
          work_model: workModelJoined,
          work_models: form.work_models,
          target_salary: fullSalary,
          skills: form.skills,
          skills_categorized: updatedCatSkills,
          experiences: form.experiences,
          education: form.education,
          certifications: form.certifications,
          profile_strength: Math.min(96, Math.max(75, form.skills.length * 4 + (form.experiences?.length || 0) * 10 + 40)),
        },
      });
      toast.success("Career Profile updated successfully!");
      setOpen(false);
    } catch (e) {
      toast.error("Could not save profile changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleImportCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/ai/import-cv-profile", formData);
      if (data?.profile) {
        const parsed = data.profile;
        const updatedSkills = Array.isArray(parsed.skills) && parsed.skills.length > 0 ? parsed.skills : rawSkills;
        const updatedName = parsed.name || user?.name || "Career Profile";

        await updateProfile({
          name: updatedName,
          profile: {
            bio: parsed.bio || userBio,
            current_role: parsed.current_role || currentRole,
            target_role: parsed.target_role || targetRole,
            years_experience: parsed.years_experience ?? yearsExp,
            locations: parsed.locations || p.locations || ["Ahmedabad, Gujarat"],
            work_model: parsed.work_model || p.work_model || "Onsite / Hybrid",
            work_models: parsed.work_models || ["Onsite", "Hybrid", "Remote"],
            target_salary: parsed.target_salary || p.target_salary || "$140k",
            skills: updatedSkills,
            skills_categorized: parsed.skills_categorized || categorizedSkills,
            experiences: parsed.experiences && parsed.experiences.length > 0 ? parsed.experiences : experiences,
            education: parsed.education && parsed.education.length > 0 ? parsed.education : education,
            certifications: parsed.certifications && parsed.certifications.length > 0 ? parsed.certifications : certifications,
            profile_strength: parsed.profile_strength || 94,
          },
        });
        toast.success(`Career Profile fully extracted from ${file.name}!`);
      } else {
        toast.error("Could not parse profile from file.");
      }
    } catch (err) {
      const msg =
        formatApiError(err?.response?.data?.detail) ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to import CV. Please check file format.";
      toast.error(msg);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt,.md"
        className="hidden"
        onChange={handleImportCv}
      />

      {/* Page Header */}
      <PageHead
        label="Career Profile"
        title="Your One Career Identity"
        sub="Your verified career credentials, work history, skill taxonomy, and target compensation."
      >
        <button
          className="cv-btn-ghost border border-border text-xs sm:text-sm flex items-center gap-1.5 shadow-xs"
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          data-testid="import-cv-btn"
        >
          {importing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-electric" /> Extracting CV with AI…
            </>
          ) : (
            <>
              <UploadCloud className="h-4 w-4 text-electric" /> Import CV
            </>
          )}
        </button>
        <button
          className="cv-btn-primary text-xs sm:text-sm flex items-center gap-1.5 shadow-glow"
          onClick={() => openEdit("overview")}
          data-testid="edit-passport-btn"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit Profile
        </button>
      </PageHead>

      {/* =========================================================================
          HERO IDENTITY CARD & EXECUTIVE OVERVIEW
         ========================================================================= */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Identity Banner (2 Cols) */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 rounded-3xl border border-border bg-gradient-to-br from-card via-card/90 to-secondary/30 p-6 sm:p-7 shadow-float relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-electric/10 via-violet/5 to-transparent rounded-full blur-2xl pointer-events-none" />

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/70 pb-5">
            <div className="flex items-center gap-4">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-electric via-violet to-aqua text-2xl font-black text-white shadow-float ring-4 ring-electric/10">
                {user?.name?.[0]?.toUpperCase() ?? "U"}
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-foreground" data-testid="passport-name">
                    {userName}
                  </h2>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-2.5 py-0.5 text-[11px] font-bold text-emerald-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Verified Identity
                  </span>
                </div>

                {/* Role Progression Badge */}
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs sm:text-sm font-semibold text-muted-foreground">
                  <span className="text-foreground">{currentRole}</span>
                  <span className="text-electric flex items-center gap-1 font-bold">
                    <ChevronRight className="h-4 w-4" /> {targetRole}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => openEdit("overview")}
              className="text-xs font-semibold text-electric hover:underline flex items-center gap-1 shrink-0"
            >
              <Pencil className="h-3 w-3" /> Quick Edit
            </button>
          </div>

          {/* Quick Meta Pills */}
          <div className="mt-4 flex flex-wrap items-center gap-2.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-secondary/50 px-3 py-1.5 font-medium text-foreground">
              <MapPin className="h-3.5 w-3.5 text-electric" /> {primaryLocation}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-secondary/50 px-3 py-1.5 font-medium text-foreground">
              <Building2 className="h-3.5 w-3.5 text-violet" />{" "}
              {Array.isArray(p.work_models) && p.work_models.length > 0
                ? p.work_models.join(" / ")
                : p.work_model || "Onsite / Hybrid"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-secondary/50 px-3 py-1.5 font-medium text-foreground">
              <Calendar className="h-3.5 w-3.5 text-coral" /> {yearsExp}+ Years Experience
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-secondary/50 px-3 py-1.5 font-medium text-foreground">
              <DollarSign className="h-3.5 w-3.5 text-lime-600" /> Target: {targetSalary}
            </span>
          </div>

          {/* Executive Career Bio */}
          <div className="mt-5 rounded-2xl border border-border/80 bg-secondary/30 p-4 sm:p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-electric" /> Executive Career Summary
            </p>
            <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed italic">
              "{userBio}"
            </p>
          </div>
        </motion.div>

        {/* Profile Strength & Health Card (1 Col) */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-border bg-card p-6 flex flex-col justify-between shadow-card"
        >
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-border/60">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Target className="h-4 w-4 text-electric" /> Profile Readiness
              </span>
              <span className="rounded-full bg-electric/10 border border-electric/25 px-2.5 py-0.5 text-[11px] font-bold text-electric">
                High Visibility
              </span>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <MatchRing value={strength} size={74} stroke={8} color="#06B6D4" />
              <div>
                <p className="font-heading text-lg font-bold text-foreground">{strength}% Complete</p>
                <p className="text-xs text-muted-foreground mt-0.5">Optimized for ATS parsers & recruiters</p>
              </div>
            </div>

            <div className="mt-5 space-y-2 text-xs">
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Verified Work Experience History</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Structured Technical Skills Matrix</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Education & Professional Certifications</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Tailored Target Role & Salary Preferences</span>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border/60">
            <button
              onClick={() => openEdit("overview")}
              className="w-full cv-btn-ghost border border-border text-xs py-2.5 justify-center flex items-center gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" /> Refine Profile Attributes
            </button>
          </div>
        </motion.div>
      </div>

      {/* =========================================================================
          SECTION 1: WORK EXPERIENCE TIMELINE
         ========================================================================= */}
      <div className="card-surface p-6 sm:p-7 shadow-card rounded-3xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border/60">
          <div>
            <h3 className="font-heading text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-electric" /> Work Experience & Career History
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Detailed chronological track record of roles, quantified achievements, and utilized technologies.
            </p>
          </div>
          <button
            onClick={() => openEdit("experience")}
            className="text-xs font-bold text-electric hover:underline flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" /> Add / Edit Experience
          </button>
        </div>

        <div className="space-y-5">
          {experiences.map((exp, idx) => (
            <div
              key={exp.id || idx}
              className="rounded-2xl border border-border/80 bg-secondary/20 p-5 sm:p-6 transition-all hover:border-electric/40 hover:bg-secondary/30 space-y-3 relative"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="font-heading text-base font-bold text-foreground">{exp.title}</h4>
                  <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground mt-0.5">
                    <span className="font-semibold text-foreground flex items-center gap-1">
                      <Building2 className="h-3.5 w-3.5 text-electric" /> {exp.company}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {exp.location || "Onsite"}
                    </span>
                    <span>•</span>
                    <span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-bold text-foreground border border-border/60">
                      {exp.type || "Full-Time"}
                    </span>
                  </div>
                </div>

                <span className="inline-flex items-center gap-1.5 rounded-full bg-electric/10 border border-electric/25 px-3 py-1 text-xs font-bold text-electric shrink-0 self-start sm:self-center">
                  <Calendar className="h-3.5 w-3.5" /> {exp.period}
                </span>
              </div>

              {exp.summary && (
                <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed">{exp.summary}</p>
              )}

              {/* Achievements Bullets */}
              {Array.isArray(exp.achievements) && exp.achievements.length > 0 && (
                <div className="pt-2 space-y-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    Key Impact & Highlights:
                  </p>
                  <ul className="space-y-1.5 text-xs text-foreground/90">
                    {exp.achievements.map((ach, aIdx) => (
                      <li key={aIdx} className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-lime-600 mt-0.5 shrink-0" />
                        <span className="leading-relaxed">{ach}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tech Stack Chips */}
              {Array.isArray(exp.technologies) && exp.technologies.length > 0 && (
                <div className="pt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-muted-foreground mr-1">Technologies:</span>
                  {exp.technologies.map((t, tIdx) => (
                    <span
                      key={tIdx}
                      className="rounded-lg bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground border border-border shadow-xs"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* =========================================================================
          SECTION 2: SKILLS TAXONOMY & DOMAIN COMPETENCIES
         ========================================================================= */}
      <div className="card-surface p-6 sm:p-7 shadow-card rounded-3xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border/60">
          <div>
            <h3 className="font-heading text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
              <Layers className="h-5 w-5 text-violet" /> Skills & Technical Taxonomy
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Organized competencies mapped to current engineering requirements and ATS keyword filters.
            </p>
          </div>
          <button
            onClick={() => openEdit("skills")}
            className="text-xs font-bold text-electric hover:underline flex items-center gap-1"
          >
            <Pencil className="h-3.5 w-3.5" /> Manage Skills ({rawSkills.length})
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 1. Languages */}
          <div className="rounded-2xl border border-electric/30 bg-electric/5 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xs sm:text-sm font-bold text-electric flex items-center gap-2">
                <Code2 className="h-4 w-4" /> Core Languages & Scripts
              </h4>
              <span className="text-[11px] font-bold text-muted-foreground">
                {categorizedSkills.languages?.length || 0} skills
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categorizedSkills.languages && categorizedSkills.languages.length > 0 ? (
                categorizedSkills.languages.map((s) => (
                  <span
                    key={s}
                    className="rounded-xl bg-card px-3 py-1.5 text-xs font-bold text-foreground border border-electric/40 shadow-xs"
                  >
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground italic">No languages tagged yet.</span>
              )}
            </div>
          </div>

          {/* 2. Frameworks & Libraries */}
          <div className="rounded-2xl border border-violet/30 bg-violet/5 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xs sm:text-sm font-bold text-violet flex items-center gap-2">
                <Cpu className="h-4 w-4" /> Frameworks & Libraries
              </h4>
              <span className="text-[11px] font-bold text-muted-foreground">
                {categorizedSkills.frameworks?.length || 0} skills
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categorizedSkills.frameworks && categorizedSkills.frameworks.length > 0 ? (
                categorizedSkills.frameworks.map((s) => (
                  <span
                    key={s}
                    className="rounded-xl bg-card px-3 py-1.5 text-xs font-bold text-foreground border border-violet/40 shadow-xs"
                  >
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground italic">No frameworks tagged yet.</span>
              )}
            </div>
          </div>

          {/* 3. Cloud, DB & DevOps */}
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xs sm:text-sm font-bold text-amber-600 dark:text-amber-400 flex items-center gap-2">
                <Server className="h-4 w-4" /> Cloud, Databases & DevOps
              </h4>
              <span className="text-[11px] font-bold text-muted-foreground">
                {categorizedSkills.cloud_tools?.length || 0} skills
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categorizedSkills.cloud_tools && categorizedSkills.cloud_tools.length > 0 ? (
                categorizedSkills.cloud_tools.map((s) => (
                  <span
                    key={s}
                    className="rounded-xl bg-card px-3 py-1.5 text-xs font-bold text-foreground border border-amber-500/40 shadow-xs"
                  >
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground italic">No cloud tools tagged yet.</span>
              )}
            </div>
          </div>

          {/* 4. Architecture & Methodologies */}
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xs sm:text-sm font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                <Workflow className="h-4 w-4" /> Architecture & Competencies
              </h4>
              <span className="text-[11px] font-bold text-muted-foreground">
                {categorizedSkills.competencies?.length || 0} skills
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categorizedSkills.competencies && categorizedSkills.competencies.length > 0 ? (
                categorizedSkills.competencies.map((s) => (
                  <span
                    key={s}
                    className="rounded-xl bg-card px-3 py-1.5 text-xs font-bold text-foreground border border-emerald-500/40 shadow-xs"
                  >
                    {s}
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground italic">No competencies tagged yet.</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================================
          SECTION 3 & 4: EDUCATION & CERTIFICATIONS (2 COLS)
         ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Education */}
        <div className="card-surface p-6 shadow-card rounded-3xl space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-border/60">
            <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-electric" /> Education & Academics
            </h3>
            <button
              onClick={() => openEdit("education")}
              className="text-xs font-bold text-electric hover:underline"
            >
              Edit
            </button>
          </div>

          <div className="space-y-3">
            {education.map((edu, idx) => (
              <div
                key={edu.id || idx}
                className="rounded-2xl border border-border/80 bg-secondary/20 p-4 space-y-1.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-heading text-sm font-bold text-foreground">{edu.degree}</h4>
                  <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-bold text-foreground shrink-0 border border-border">
                    {edu.year}
                  </span>
                </div>
                <p className="text-xs font-semibold text-electric">{edu.field}</p>
                <p className="text-xs text-muted-foreground">{edu.institution}</p>
                {edu.grade && (
                  <p className="text-[11px] font-bold text-emerald-500 pt-1">Grade: {edu.grade}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="card-surface p-6 shadow-card rounded-3xl space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-border/60">
            <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" /> Certifications & Accreditations
            </h3>
            <button
              onClick={() => openEdit("education")}
              className="text-xs font-bold text-electric hover:underline"
            >
              Edit
            </button>
          </div>

          <div className="space-y-3">
            {certifications.map((cert, idx) => (
              <div
                key={cert.id || idx}
                className="rounded-2xl border border-border/80 bg-secondary/20 p-4 space-y-1.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-heading text-sm font-bold text-foreground">{cert.name}</h4>
                  <span className="rounded-full bg-amber-500/10 border border-amber-500/25 px-2.5 py-0.5 text-[11px] font-bold text-amber-600 dark:text-amber-300 shrink-0">
                    {cert.year}
                  </span>
                </div>
                <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                  <Building2 className="h-3 w-3 text-electric" /> {cert.issuer}
                </p>
                <p className="text-[11px] font-bold text-emerald-500 flex items-center gap-1 pt-0.5">
                  <CheckCircle2 className="h-3 w-3" /> Verified Credential
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* =========================================================================
          SECTION 5: CAREER SEARCH & COMPENSATION PREFERENCES
         ========================================================================= */}
      <div className="card-surface p-6 sm:p-7 shadow-card rounded-3xl space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-border/60">
          <div>
            <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-electric" /> Job Search & Opportunity Criteria
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Preferences automatically applied across ATS Checker, Find Jobs, and Interview Copilot.
            </p>
          </div>
          <button
            onClick={() => openEdit("preferences")}
            className="text-xs font-bold text-electric hover:underline"
          >
            Edit Preferences
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
          <div className="rounded-2xl border border-border/80 bg-secondary/20 p-4 space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Target Role</span>
            <p className="font-heading text-sm font-bold text-foreground">{targetRole}</p>
            <p className="text-xs text-muted-foreground">Level: Senior / Lead</p>
          </div>

          <div className="rounded-2xl border border-border/80 bg-secondary/20 p-4 space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Target Compensation</span>
            <p className="font-heading text-sm font-bold text-lime-600 dark:text-lime">{targetSalary}</p>
            <p className="text-xs text-muted-foreground">Base + Bonus Benchmark</p>
          </div>

          <div className="rounded-2xl border border-border/80 bg-secondary/20 p-4 space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Work Model</span>
            <p className="font-heading text-sm font-bold text-foreground">
              {Array.isArray(p.work_models) && p.work_models.length > 0
                ? p.work_models.join(", ")
                : p.work_model || "Onsite, Hybrid"}
            </p>
            <p className="text-xs text-muted-foreground">Open to flexible arrangements</p>
          </div>

          <div className="rounded-2xl border border-border/80 bg-secondary/20 p-4 space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Location Alignment</span>
            <p className="font-heading text-sm font-bold text-foreground">{primaryLocation}</p>
            <p className="text-xs text-muted-foreground">Primary Residence & Relocation</p>
          </div>
        </div>
      </div>

      {/* =========================================================================
          COMPREHENSIVE MULTI-TAB EDIT PROFILE MODAL
         ========================================================================= */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl p-6 sm:p-7" data-testid="edit-passport-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl sm:text-2xl font-extrabold flex items-center gap-2">
              <Pencil className="h-5 w-5 text-electric" /> Edit Career Identity Profile
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Update your career information to optimize job matches, AI interview simulations, and ATS scoring.
            </p>
          </DialogHeader>

          {/* Modal Navigation Tabs */}
          <div className="flex border-b border-border gap-2 overflow-x-auto pb-1 mt-2">
            {[
              { id: "overview", label: "Basic Info & Bio" },
              { id: "experience", label: `Experience (${form.experiences?.length || 0})` },
              { id: "skills", label: `Skills (${form.skills?.length || 0})` },
              { id: "education", label: "Education & Certs" },
              { id: "preferences", label: "Job Preferences" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-3 text-xs font-bold rounded-t-lg transition-all shrink-0 ${
                  activeTab === tab.id
                    ? "border-b-2 border-electric text-electric bg-electric/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab 1: Overview & Bio */}
          {activeTab === "overview" && (
            <div className="space-y-4 pt-3 animate-in fade-in duration-200">
              <EditField
                label="Full Name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                testid="edit-name"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <EditField
                  label="Current Role / Title"
                  value={form.current_role}
                  onChange={(e) => set("current_role", e.target.value)}
                  testid="edit-current-role"
                />
                <EditField
                  label="Target Role / Next Step"
                  value={form.target_role}
                  onChange={(e) => set("target_role", e.target.value)}
                  testid="edit-target-role"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <EditField
                  label="Years of Experience"
                  type="number"
                  value={form.years_experience}
                  onChange={(e) => set("years_experience", e.target.value)}
                  testid="edit-years"
                />
                <EditField
                  label="Primary Location (City, Country)"
                  value={form.location}
                  onChange={(e) => set("location", e.target.value)}
                  testid="edit-location"
                />
              </div>

              <div>
                <label className="label-caps block mb-1">Executive Career Summary / Bio</label>
                <textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => set("bio", e.target.value)}
                  placeholder="2-3 sentences summarizing your professional background and core strengths..."
                  className="w-full rounded-xl border border-border bg-background p-3 text-xs sm:text-sm leading-relaxed outline-none focus:border-electric"
                />
              </div>
            </div>
          )}

          {/* Tab 2: Work Experience */}
          {activeTab === "experience" && (
            <div className="space-y-4 pt-3 animate-in fade-in duration-200">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">Manage your work history entries</p>
                <button
                  type="button"
                  onClick={addExperienceItem}
                  className="cv-btn-ghost border border-border text-xs py-1.5 px-3 flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add New Role
                </button>
              </div>

              <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
                {form.experiences?.map((exp, idx) => (
                  <div key={exp.id || idx} className="rounded-xl border border-border p-4 bg-secondary/20 space-y-3 relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-electric">Role #{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeExperienceItem(idx)}
                        className="text-muted-foreground hover:text-rose-500 p-1"
                        title="Delete Role"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <EditField
                        label="Job Title"
                        value={exp.title || ""}
                        onChange={(e) => updateExperienceItem(idx, "title", e.target.value)}
                      />
                      <EditField
                        label="Company Name"
                        value={exp.company || ""}
                        onChange={(e) => updateExperienceItem(idx, "company", e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <EditField
                        label="Period (e.g. 2022 – Present)"
                        value={exp.period || ""}
                        onChange={(e) => updateExperienceItem(idx, "period", e.target.value)}
                      />
                      <EditField
                        label="Location & Type"
                        value={exp.location || ""}
                        onChange={(e) => updateExperienceItem(idx, "location", e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="label-caps block mb-1">Role Summary</label>
                      <input
                        type="text"
                        value={exp.summary || ""}
                        onChange={(e) => updateExperienceItem(idx, "summary", e.target.value)}
                        placeholder="Brief summary of main responsibility"
                        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs outline-none focus:border-electric"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab 3: Skills */}
          {activeTab === "skills" && (
            <div className="space-y-4 pt-3 animate-in fade-in duration-200">
              <div>
                <label className="label-caps">Current Active Skills</label>
                <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 rounded-xl bg-secondary/30 border border-border">
                  {form.skills.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border px-3 py-1 text-xs font-semibold"
                    >
                      {s}
                      <button
                        type="button"
                        onClick={() => removeSkill(s)}
                        data-testid={`remove-skill-${s}`}
                        className="text-muted-foreground hover:text-rose-500"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3">
                <input
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSkill();
                    }
                  }}
                  placeholder="Type a skill name and press Enter (e.g. Java, Docker, React, AWS)"
                  data-testid="edit-skill-input"
                  className="w-full bg-transparent py-2.5 text-xs sm:text-sm outline-none"
                />
                <button
                  type="button"
                  onClick={addSkill}
                  data-testid="add-skill-btn"
                  className="rounded-full bg-electric p-1.5 text-white shrink-0 shadow-xs"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Tab 4: Education & Certs */}
          {activeTab === "education" && (
            <div className="space-y-5 pt-3 animate-in fade-in duration-200 max-h-[50vh] overflow-y-auto pr-1">
              {/* Education Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading text-xs sm:text-sm font-bold text-foreground">Degrees & Education</h4>
                  <button
                    type="button"
                    onClick={addEducationItem}
                    className="text-xs font-bold text-electric hover:underline flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add Degree
                  </button>
                </div>

                {form.education?.map((edu, idx) => (
                  <div key={edu.id || idx} className="rounded-xl border border-border p-3.5 bg-secondary/20 space-y-2 relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-electric">Degree #{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeEducationItem(idx)}
                        className="text-muted-foreground hover:text-rose-500 p-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <EditField
                        label="Degree"
                        value={edu.degree || ""}
                        onChange={(e) => updateEducationItem(idx, "degree", e.target.value)}
                      />
                      <EditField
                        label="Field of Study"
                        value={edu.field || ""}
                        onChange={(e) => updateEducationItem(idx, "field", e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <EditField
                        label="Institution / University"
                        value={edu.institution || ""}
                        onChange={(e) => updateEducationItem(idx, "institution", e.target.value)}
                      />
                      <EditField
                        label="Year & Grade"
                        value={edu.year || ""}
                        onChange={(e) => updateEducationItem(idx, "year", e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Certifications Section */}
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading text-xs sm:text-sm font-bold text-foreground">Certifications</h4>
                  <button
                    type="button"
                    onClick={addCertItem}
                    className="text-xs font-bold text-electric hover:underline flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add Certificate
                  </button>
                </div>

                {form.certifications?.map((cert, idx) => (
                  <div key={cert.id || idx} className="rounded-xl border border-border p-3.5 bg-secondary/20 space-y-2 relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-amber-500">Certificate #{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeCertItem(idx)}
                        className="text-muted-foreground hover:text-rose-500 p-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <EditField
                        label="Certificate Name"
                        value={cert.name || ""}
                        onChange={(e) => updateCertItem(idx, "name", e.target.value)}
                      />
                      <EditField
                        label="Issuing Organization"
                        value={cert.issuer || ""}
                        onChange={(e) => updateCertItem(idx, "issuer", e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab 5: Job Preferences */}
          {activeTab === "preferences" && (
            <div className="space-y-4 pt-3 animate-in fade-in duration-200">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-caps">Preferred Work Models</label>
                  <span className="text-[11px] text-muted-foreground">Select all that apply</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {WORK_MODELS.map((m) => {
                    const isSelected = (form.work_models || []).includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleWorkModel(m)}
                        className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-all ${
                          isSelected
                            ? "border-electric bg-electric text-white shadow-xs"
                            : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5 stroke-[2.5]" />}
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="label-caps block mb-1.5">Target Annual Salary / Compensation</label>
                <div className="flex gap-2">
                  <select
                    value={form.salary_currency || "USD"}
                    onChange={(e) => set("salary_currency", e.target.value)}
                    className="shrink-0 rounded-xl border border-border bg-card px-3 py-2.5 text-xs font-semibold focus:border-electric focus:outline-none"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} ({c.symbol})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={form.target_salary}
                    onChange={(e) => set("target_salary", e.target.value)}
                    placeholder="140k"
                    data-testid="edit-salary"
                    className="flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-xs font-medium focus:border-electric outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 pt-3 border-t border-border flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="cv-btn-ghost border border-border text-xs">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-passport-btn"
              className="cv-btn-primary text-xs shadow-glow flex items-center gap-1.5"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving Changes…
                </>
              ) : (
                "Save Career Profile"
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditField({ label, testid, ...props }) {
  return (
    <div>
      <label className="label-caps block mb-1">{label}</label>
      <input
        {...props}
        data-testid={testid}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs sm:text-sm outline-none focus:border-electric transition-colors"
      />
    </div>
  );
}
